import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  BatteryCharging,
  ChevronDown,
  Clock,
  Cpu,
  Droplets,
  ShieldCheck,
  Smartphone,
  Wrench,
  Zap,
} from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, SectionLabel } from "@/components/Reveal";

const services = [
  {
    icon: Smartphone,
    title: "Display-Reparatur",
    text: "Gesprungenes Glas, Risse im Display oder Touchscreen ohne Funktion – wir tauschen dein Display mit Original- oder qualitativ gleichwertigen Ersatzteilen.",
    tag: "Beliebteste Reparatur",
  },
  {
    icon: BatteryCharging,
    title: "Akku-Wechsel",
    text: "Dein Handy hält keinen Tag mehr durch? Ein neuer Akku bringt deinem Gerät die volle Leistung zurück – schnell und unkompliziert.",
    tag: "Oft in ~1 Stunde",
  },
  {
    icon: Cpu,
    title: "Software & Daten",
    text: "Hängende Apps, Start-Probleme oder Update-Fehler: Wir analysieren das Problem und bringen dein System wieder zum Laufen.",
    tag: "Smartphone & Tablet",
  },
  {
    icon: Zap,
    title: "Express-Reparatur",
    text: "Viele Reparaturen erledigen wir innerhalb von nur 1 Stunde. Kurz vorbeikommen, Kaffee trinken, fertiges Gerät mitnehmen.",
    tag: "1h Service",
  },
  {
    icon: Droplets,
    title: "Wasserschaden-Check",
    text: "Gerät nass geworden? Schnelles Handeln zählt. Wir prüfen den Schaden ehrlich und sagen dir, was noch zu retten ist.",
    tag: "Ehrliche Diagnose",
  },
  {
    icon: Wrench,
    title: "Diagnose & Kostenvoranschlag",
    text: "Du bekommst vor jeder Reparatur einen transparenten Kostenvoranschlag. Erst wenn du zustimmst, wird geschraubt.",
    tag: "Kostenlos & unverbindlich",
  },
];

const faqs = [
  {
    q: "Wie lange dauert eine Reparatur?",
    a: "Viele Reparaturen erledigen wir innerhalb von 1 Stunde. Die genaue Dauer hängt von Ersatzteilverfügbarkeit, Schadensumfang und Gerätezustand ab – Zeitangaben sind daher unverbindlich, aber wir halten dich immer auf dem Laufenden.",
  },
  {
    q: "Bekomme ich eine Garantie auf die Reparatur?",
    a: "Ja. HeroPhone gewährt auf durchgeführte Reparaturen eine 90-Tage Geld-zurück-Garantie, sofern ein Fehler der durchgeführten Reparatur vorliegt. Innerhalb dieser Zeit wird kostenlos nachgebessert oder der Reparaturpreis erstattet. Die Garantie gilt für das ersetzte Bauteil und die durchgeführte Reparatur.",
  },
  {
    q: "Gehen bei der Reparatur meine Daten verloren?",
    a: "In der Regel bleiben deine Daten erhalten. Trotzdem gilt: Du bist selbst für die Sicherung deiner Daten verantwortlich. Sichere vor der Reparatur Fotos, Kontakte und Apps – wir übernehmen keine Haftung für Datenverlust.",
  },
  {
    q: "Welche Ersatzteile verwendet ihr?",
    a: "Je nach Verfügbarkeit verwenden wir Originalersatzteile oder qualitativ gleichwertige Ersatzteile. Auf Anfrage ist auch günstigere Qualität möglich – diese ist dann von der Garantie ausgeschlossen. Wir beraten dich ehrlich, was sich für dein Gerät lohnt.",
  },
  {
    q: "Bleibt mein Handy nach der Reparatur wasserdicht?",
    a: "Viele moderne Smartphones haben eine Wasserschutz-Zertifizierung (z. B. IP67/IP68). Durch das Öffnen des Gerätes kann diese Schutzfunktion nicht garantiert erhalten bleiben – wir können daher keine Haftung für Wasserschäden nach einer Reparatur übernehmen.",
  },
  {
    q: "Funktionieren FaceID / TouchID nach der Reparatur noch?",
    a: "Bei bestimmten Geräten (z. B. Apple iPhone) können Funktionen wie FaceID, TouchID oder der Fingerabdrucksensor durch Herstellerschutzmechanismen eingeschränkt werden. Dafür können wir keine Garantie übernehmen, sofern dies technisch durch den Hersteller begründet ist.",
  },
];

const Services = () => {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div data-testid="services-page">
      {/* HEADER */}
      <section className="relative bg-blueprint pt-40 pb-20 overflow-hidden">
        <div
          className="absolute -top-32 right-0 w-[480px] h-[480px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,230,0,0.1) 0%, transparent 65%)" }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <SectionLabel>Leistungen</SectionLabel>
            <h1 className="mt-5 font-display text-6xl sm:text-7xl lg:text-8xl font-extrabold uppercase text-white leading-[0.9] max-w-4xl">
              Präzisions-Reparaturen für <span className="text-[#FFE600]">Smartphone &amp; Tablet</span>
            </h1>
            <p className="mt-7 text-gray-400 text-base sm:text-lg leading-relaxed max-w-2xl">
              Von Displays über Batterien bis hin zu Softwareproblemen – HeroPhone
              bietet umfassende Reparaturservices für deine mobilen Geräte.
              Transparent, ehrlich und mit kostenlosem Kostenvoranschlag.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SERVICE CARDS */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.08}>
                <div
                  data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="glow-card group bg-[#111318] rounded-md p-8 h-full flex flex-col"
                >
                  <div className="flex items-start justify-between">
                    <s.icon className="w-8 h-8 text-[#FFE600]" strokeWidth={1.75} />
                    <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-gray-500 border border-white/10 rounded-full px-3 py-1">
                      {s.tag}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-3xl font-bold uppercase text-white">{s.title}</h3>
                  <p className="mt-3 text-gray-400 text-sm leading-relaxed flex-1">{s.text}</p>
                  <Link
                    to="/kontakt"
                    data-testid={`service-link-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group/link mt-6 inline-flex items-center gap-2 font-mono2 text-[11px] tracking-[0.2em] uppercase text-[#FFE600]"
                  >
                    Anfragen
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover/link:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EXPRESS + GARANTIE */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-5">
          <Reveal>
            <div className="relative bg-[#FFE600] rounded-md p-9 sm:p-12 overflow-hidden h-full" data-testid="express-promise-card">
              <Clock className="w-10 h-10 text-[#090A0C]" strokeWidth={1.75} />
              <h3 className="mt-6 font-display text-4xl sm:text-5xl font-extrabold uppercase text-[#090A0C] leading-[0.95]">
                1-Stunden Express-Service
              </h3>
              <p className="mt-4 text-[#090A0C]/80 leading-relaxed max-w-md">
                Reparaturen häufig innerhalb von 1 Stunde. Du bringst dein Gerät
                vorbei – wir machen den Rest. Schneller geht ehrlicher Service nicht.
              </p>
              <div className="mt-6 flex items-center gap-2.5 font-mono2 text-[11px] tracking-[0.2em] uppercase text-[#090A0C]">
                <span className="w-2 h-2 rounded-full bg-[#090A0C] animate-pulse-dot" />
                Express verfügbar
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="relative bg-[#111318] border border-[#FFE600]/30 rounded-md p-9 sm:p-12 overflow-hidden h-full" data-testid="warranty-promise-card">
              <ShieldCheck className="w-10 h-10 text-[#FFE600]" strokeWidth={1.75} />
              <h3 className="mt-6 font-display text-4xl sm:text-5xl font-extrabold uppercase text-white leading-[0.95]">
                90 Tage <span className="text-[#FFE600]">Geld-zurück</span>
              </h3>
              <p className="mt-4 text-gray-400 leading-relaxed max-w-md">
                Hohe Kundenzufriedenheit ist unser oberstes Ziel: Liegt ein Fehler
                der durchgeführten Reparatur vor, bessern wir kostenlos nach oder
                erstatten den Reparaturpreis – 90 Tage lang.
              </p>
              <p className="mt-6 font-mono2 text-[11px] tracking-[0.2em] uppercase text-gray-500">
                Gilt für ersetztes Bauteil &amp; durchgeführte Reparatur
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="py-20">
        <Marquee
          items={[
            "Displays",
            "Akkus",
            "Software",
            "Express-Service",
            "90 Tage Garantie",
            "Ehrliche Diagnose",
          ]}
        />
      </div>

      {/* FAQ */}
      <section className="py-20 sm:py-24 bg-[#0B0D11]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>Häufige Fragen</SectionLabel>
            <h2 className="mt-5 font-display text-5xl sm:text-6xl font-extrabold uppercase text-white leading-[0.92]">
              Klartext, <span className="text-[#FFE600]">bevor du fragst.</span>
            </h2>
          </Reveal>

          <div className="mt-12 space-y-3" data-testid="faq-accordion">
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <Reveal key={f.q} delay={i * 0.05}>
                  <div className={`rounded-md border transition-colors duration-300 ${open ? "border-[#FFE600]/60 bg-[#111318]" : "border-white/10 bg-[#111318]/60"}`}>
                    <button
                      data-testid={`faq-question-${i}`}
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      className="w-full flex items-center justify-between gap-6 text-left px-6 sm:px-8 py-6"
                    >
                      <span className="font-display text-xl sm:text-2xl font-semibold uppercase text-white">
                        {f.q}
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 shrink-0 text-[#FFE600] transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <p className="px-6 sm:px-8 pb-7 text-gray-400 text-sm sm:text-base leading-relaxed" data-testid={`faq-answer-${i}`}>
                            {f.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1} className="mt-14 text-center">
            <Link
              to="/kontakt"
              data-testid="faq-cta-button"
              className="inline-flex items-center gap-3 bg-[#FFE600] text-[#090A0C] font-display font-bold uppercase tracking-wide text-lg px-8 py-4 rounded-sm hover:bg-[#FFF033] hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200"
            >
              Noch Fragen? Schreib uns
              <ArrowRight className="w-5 h-5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Services;
